/**
 * CSV 解析工具
 * 解决批量导入中的三类数据丢失问题：
 * 1. GBK/ANSI 编码文件（中文 Windows Excel 默认导出）按 UTF-8 读取导致中文乱码
 * 2. 朴素 split(',') 无法处理引号包裹字段（字段内逗号导致列错位）
 * 3. 行尾 \r（Windows CRLF 换行）混入最后一列数据
 */

/**
 * 以正确编码读取 CSV 文本文件
 * 先尝试 UTF-8（fatal 模式，遇到非法序列抛错），失败则回退 GBK
 * @param file 用户选择的文件
 * @returns 解码后的文本
 */
export async function readCsvText(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  try {
    // fatal: true 时非法 UTF-8 字节序列会抛出 TypeError
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    // 中文 Windows Excel 导出的 CSV 通常是 GBK 编码
    return new TextDecoder('gbk').decode(buffer);
  }
}

/**
 * 解析单行 CSV（兼容引号包裹字段与转义引号）
 * @param line 单行文本（不含换行符）
 * @returns 字段数组
 */
export function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        // 双引号转义（"" → "）
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

/**
 * 将 CSV 全文拆分为行（兼容 CRLF / LF / CR 换行）
 * @param text CSV 全文
 * @returns 非空行数组
 */
export function splitCsvLines(text: string): string[] {
  return text.split(/\r\n|\r|\n/).filter(line => line.trim());
}
